<template>
  <div class="row">
    <div class="col s10">
      <div class="col s12 z-depth-1 white">
        <div class="row">
          <div class="left" style="padding-top: 10px;">
            <span class="title" style="padding-left: 11px;">{{ $t('analysis.histogram') }}</span>
          </div>
        </div>
        <Histogram :rail-object="railObject"
                   :api-data="apiData"
                   :metric="metric"
                   :metric-translated="getTranslationKey(metric)"
                   :unit="getUnit()"
                   :bin-size="binSize"
                   @on-select="onSelect"/>
      </div>
    </div>
    <div class="col s2">
      <div class="control col s12 z-depth-1 white">
        <div class="analysis-control">
          <div class="input-field">
            {{ $t('analysis.property') }}:
            <select v-model="metric" class="browser-default">
              <option v-for="property in propertiesPlusDate" :value="property" :key="property">
                {{ getTranslationKey(property) }}
              </option>
            </select>
          </div>
          <div class="input-field">
            {{ $t('analysis.binSize') }}
            <input id="h-bin" type="text" v-model="binSize">
             <div class="col s9 error" v-if="!$v.binSize.required">{{$t('management.requiredfield')}}</div>
             <div class="col s9 error" v-if="!$v.binSize.decimal">{{$t('management.notnumeric')}}</div>
          </div>
        </div>
        <hr />
        <div v-show="metric !== 'Date'">
          <table class="table-condensed" style="background-color: #eee">
            <tbody>
              <tr>
                <td>{{this.getTranslationKey('min')}}</td>
                <td>{{ values.min }} {{ this.getUnit()}}</td>
              </tr>
              <tr>
                <td>{{this.getTranslationKey('max')}}</td>
                <td>{{ values.max }} {{ this.getUnit()}}</td>
              </tr>
              <tr>
                <td>{{this.getTranslationKey('avg')}}</td>
                <td>{{ values.avg }} {{ this.getUnit()}}</td>
              </tr>
              <tr>
                <td>{{this.getTranslationKey('med')}}</td>
                <td>{{ values.med }} {{ this.getUnit()}}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Histogram from './Histogram'
import SwitchApi from '@/services/api/switch/SwitchApi'
import moment from 'moment'
import { decimal, required } from 'vuelidate/lib/validators'
import AlarmService from '@/services/AlarmService'

export default {
  name: 'HistogramAnalysis',
  components: {
    Histogram
  },
  props: {
    railObject: {
      required: true
    },
    timeSelection: {
      required: true
    }
  },
  data () {
    return {
      metric: 'PowerTotal',
      binSize: 1,
      properties: [],
      selected: [],
      apiData: [],
      sensors: null
    }
  },
  created: function () {
    this.getSensor()
    this.getAnalysisData()
  },
  computed: {
    propertiesPlusDate: function () {
      const exclude = ['MeasurementDateTime', 'RailObjectId', 'MeasurementId', 'Direction', 'InControl']
      const excludeSimpleSensor = ['MeasurementDateTime', 'RailObjectId', 'MeasurementId', 'Direction', 'InControl', 'Date', 'PowerStart', 'PowerMid', 'PowerEnd', 'PowerDiff', 'PowerTotal', 'AmpMiddle']
      let result = []

      if (this.sensors === null || this.sensors.ConfigSensor === null || this.sensors.ConfigSensor.UnitOfMeasure === null) {
        return result
      }

      if (this.sensors.ConfigSensor.UnitOfMeasure.toLowerCase() === 'bar'.toLowerCase() ||
          this.sensors.ConfigSensor.UnitOfMeasure.toLowerCase() === 'mm'.toLowerCase() ||
          this.sensors.ConfigSensor.UnitOfMeasure.toLowerCase() === 'kN'.toLowerCase() ||
          this.sensors.ConfigSensor.UnitOfMeasure.toLowerCase() === 'deg'.toLowerCase() ||
          this.sensors.ConfigSensor.UnitOfMeasure.toLowerCase() === 'dB'.toLowerCase() ||
          this.sensors.ConfigSensor.UnitOfMeasure.toLowerCase() === 'G'.toLowerCase()) {
        this.properties.forEach(x => {
          if (excludeSimpleSensor.indexOf(x) === -1) {
            result.push(x)
          }
        })
      } else {
        this.properties.forEach(x => {
          if (exclude.indexOf(x) === -1) {
            result.push(x)
          }
        })
      }
      return result
    },
    values () {
      let data = this.apiData.filter(x => (this.filterForDirEng(x)))
      let min
      let max
      let sum
      let count
      let avg
      let med
      if (data && data[0]) {
        min = data[0][this.metric]
        max = data[0][this.metric]
        sum = data[0][this.metric]
        count = 0
        med = data[0][this.metric]
        data.forEach(d => {
          if (d[this.metric] < min) {
            min = d[this.metric]
          }
          if (d[this.metric] > max) {
            max = d[this.metric]
          }
          sum += d[this.metric]
          count++
        })
        avg = sum / count

        data = data.sort((a, b) => a[this.metric] - b[this.metric])
        let half = Math.floor(data.length / 2)
        if (data.length % 2) {
          med = data[half][this.metric]
        } else {
          med = (data[half - 1][this.metric] + data[half][this.metric]) / 2.0
        }
      } else {
        min = '0'
        max = '0'
        avg = '0'
        med = '0'
      }
      return {
        min: parseFloat(min).toFixed(this.getResolution()),
        max: parseFloat(max).toFixed(this.getResolution()),
        avg: parseFloat(avg).toFixed(this.getResolution()),
        med: parseFloat(med).toFixed(this.getResolution())
      }
    }
  },
  methods: {
    filterForDirEng (measurement) {
      return (this.selected.findIndex(s => s.dir.toString(10) === measurement.Direction.toString() && s.eng.toString() === measurement.Engine.toString()) !== -1)
    },
    getAnalysisData () {
      SwitchApi.getSwitchAnalysisData(
        this.railObject.RailObjectId,
        moment(this.timeSelection.start).format('YYYY-MM-DDTHH:mm:ss'),
        moment(this.timeSelection.end).format('YYYY-MM-DDTHH:mm:ss'))
        .then(result => {
          this.apiData = result.data

          if (this.apiData[0]) {
            this.properties = Object.keys(this.apiData[0])
          }
        })
    },
    getResolution () {
      switch (this.metric) { // assume metrics won't change case
        case 'Temperature':
          return 1
        case 'Engine':
          return 0
        case 'PowerTotal':
          return 0
        case 'PowerStart':
          return 0
        case 'PowerMid':
          return 0
        case 'PowerEnd':
          return 0
        case 'PowerDiff':
          return 0
        case 'Duration':
          return 3
        case 'AmpStart':
          return 2
        case 'AmpMiddle':
          return 2
        case 'AmpEnd':
          return 2
        case 'Alarm':
          return 2
        default:
          return 4
      }
    },
    setBinSize () {
      switch (this.metric) {
        case 'Duration':
          this.binSize = 0.25
          return
        case 'AmpStart':
        case 'AmpMiddle':
        case 'AmpEnd':
          switch (this.sensors.ConfigSensor.UnitOfMeasure.toLowerCase()) {
            case 'bar'.toLowerCase():
            case 'mm'.toLowerCase():
            case 'G'.toLowerCase():
            case 'kN'.toLowerCase():
              this.binSize = 0.001
              return
            case 'deg'.toLowerCase():
              this.binSize = 0.25
              return
            case 'dB'.toLowerCase():
              this.binSize = 0.01
              return
            default:
              this.binSize = 0.1
              return
          }
        default:
          this.binSize = 1
      }
    },
    getUnit () {
      if (this.sensors === null || this.sensors.ConfigSensor === null || this.sensors.ConfigSensor.UnitOfMeasure === null) {
        return ''
      }
      switch (this.metric) {
        case 'PowerStart':
        case 'PowerMid':
        case 'PowerEnd':
        case 'PowerDiff':
        case 'PowerTotal':
        case 'Engines':
        case 'Date':
        case 'Alarm':
          return ' '
        case 'Temperature':
          return '(°C)'
        case 'Duration':
          return '(s)'
        default:
          return '(' + this.sensors.ConfigSensor.UnitOfMeasure + ')'
      }
    },
    getTranslationKey (val) {
      switch (val) {
        case 'Date':
          return this.$t('views.alarmlist.date')
        case 'Temperature':
          return this.$t('views.switch.temperature')
        case 'Engine':
          return this.$t('views.switch.engines')
        case 'Duration':
          return this.$t('views.switch.duration')
        case 'Alarm':
          return this.$t('alarm')
        case 'frequency':
          return this.$t('views.switch.frequency')
        case 'min':
          return this.$t('views.switch.min')
        case 'max':
          return this.$t('views.switch.max')
        case 'avg':
          return this.$t('views.switch.avg')
        case 'med':
          return this.$t('views.switch.med')
        case 'AmpStart':
          if (this.sensors === null) {
            return 'AmpStart'
          }
          switch (this.sensors.ConfigSensor.UnitOfMeasure.toLowerCase()) {
            case 'bar'.toLowerCase():
              return this.$t('views.switch.pressurestart')
            case 'mm'.toLowerCase():
              return this.$t('views.switch.positionstart')
            case 'kN'.toLowerCase():
              return this.$t('views.switch.forcestart')
            case 'deg'.toLowerCase():
              return this.$t('views.switch.temperature') + ' ' + this.$t('views.switch.sections.start')
            case 'dB'.toLowerCase():
              return this.$t('views.switch.noicestart')
            case 'G'.toLowerCase():
              return this.$t('views.switch.forcestart')
            default:
              return 'AmpStart'
          }
        case 'AmpEnd':
          if (this.sensors === null) {
            return 'AmpEnd'
          }
          switch (this.sensors.ConfigSensor.UnitOfMeasure.toLowerCase()) {
            case 'bar'.toLowerCase():
              return this.$t('views.switch.pressureend')
            case 'mm'.toLowerCase():
              return this.$t('views.switch.positionend')
            case 'kN'.toLowerCase():
              return this.$t('views.switch.forceend')
            case 'deg'.toLowerCase():
              return this.$t('views.switch.temperature') + ' ' + this.$t('views.switch.sections.end')
            case 'dB'.toLowerCase():
              return this.$t('views.switch.noiceend')
            case 'G'.toLowerCase():
              return this.$t('views.switch.forceend')
            default:
              return 'AmpEnd'
          }
        default:
          return val
      }
    },
    onSelect (newVal) {
      this.selected = newVal
    },
    getSensor () {
      if (this.railObject.RailObjectId) {
        this.sensors = null
        AlarmService.GetSensorsByRailobjectId(this.railObject.RailObjectId).then(result => {
          result.data.forEach(el => {
            if (el.RailObjectId === this.railObject.RailObjectId) {
              this.sensors = el
            }
          })
        })
      }
    }
  },
  validations: {
    binSize: {
      decimal,
      required
    }
  },
  watch: {
    metric () {
      this.setBinSize()
    },
    railObject () {
      this.getSensor()
      this.getAnalysisData()
      this.setBinSize()
    },
    timeSelection () {
      this.getSensor()
      this.getAnalysisData()
      this.setBinSize()
    }
  }
}
</script>

<style scoped>
.error {
    font-size: 12px;
    color:red;
  }
</style>



// WEBPACK FOOTER //
// src/components/switch/analysis/HistogramAnalysis.vue